import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import { FaWhatsapp, FaInstagram, FaFacebook, FaTiktok, FaYoutube, FaTrash, FaCheck, FaReply, FaThumbsUp, FaThumbsDown, FaHeart, FaPencilAlt } from "react-icons/fa";

// Photos are served as WebP only: /<name>.webp (full size) plus /<name>-<width>.webp copies,
// all made by scripts/images.js from the JPEGs in foto-surse/ — keep the widths in sync with it.
const IMAGES = {
  "dr-florin-georgescu-medic-chirurg": { widths: [480, 720], full: 996 },
  "dr-florin-georgescu-sala-de-operatie": { widths: [640], full: 1024 },
  "dr-florin-georgescu-logo": { widths: [120, 300], full: 500 },
  "dr-florin-georgescu-interventie-chirurgicala": { widths: [640, 1000], full: 1600 },
};
// Photo alt text leads with the search phrase (Google Images); the visible captions stay as they are.
const ALT_PREFIX = { ro: "Chirurgie generală București", en: "General surgery Bucharest" };
const imgSrc = name => `/${name}.webp`;
const imgSrcSet = name => [...IMAGES[name].widths.map(w => `/${name}-${w}.webp ${w}w`), `/${name}.webp ${IMAGES[name].full}w`].join(", ");

function ReactionBar({ entityType, entity, userReactions, onReact }) {
  const reactions = [
    { type: 'like',   icon: <FaThumbsUp size={11} />,   activeColor: '#167485', activeBg: '#e0f7fb' },
    { type: 'heart',  icon: <FaHeart size={11} />,       activeColor: '#e53935', activeBg: '#fce4ec' },
    { type: 'unlike', icon: <FaThumbsDown size={11} />,  activeColor: '#fb8c00', activeBg: '#fff3e0' },
  ];
  return (
    <div style={{ display: 'flex', gap: '6px' }}>
      {reactions.map(({ type, icon, activeColor, activeBg }) => {
        const active = userReactions.has(`${entityType}_${entity.id}_${type}`);
        const count = entity[type === 'unlike' ? 'unlikes' : type + 's'] || 0;
        return (
          <button
            key={type}
            onClick={() => onReact(entityType, entity.id, type)}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '3px 9px', border: 'none', borderRadius: '12px', cursor: 'pointer',
              fontSize: '12px', fontWeight: active ? '600' : '400',
              background: active ? activeBg : '#f0eeeb',
              color: active ? activeColor : '#6b6b6b',
              transition: 'all 0.15s',
            }}
          >
            {icon} {count}
          </button>
        );
      })}
    </div>
  );
}

export default function SurgeonSite() {
  const [lang, setLang] = useState("ro");
  const [activeNav, setActiveNav] = useState("home");
  const [isScrolled, setIsScrolled] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const isAdmin = adminToken !== null;
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminError, setAdminError] = useState("");

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState("");
  const [commentLastPostTime, setCommentLastPostTime] = useState(0);

  // Q&A state
  const [questions, setQuestions] = useState([]);
  const [questionInput, setQuestionInput] = useState("");
  const [questionLastPostTime, setQuestionLastPostTime] = useState(0);
  const [replyInput, setReplyInput] = useState({});
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [commentUsername, setCommentUsername] = useState("");
  const [questionUsername, setQuestionUsername] = useState("");
  const [toast, setToast] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const galleryTouchX = useRef(null);
  const [submissionAlert, setSubmissionAlert] = useState(null);
  const [commentsVisible, setCommentsVisible] = useState(5);
  const [questionsVisible, setQuestionsVisible] = useState(5);
  const questionTouchX = useRef({});
  const [showGdpr, setShowGdpr] = useState(false);
  const gdprRef = useRef(null);

  const galleryImages = [
    {
      name: "dr-florin-georgescu-sala-de-operatie",
      captionRo: "Dr. Georgescu în sala de operație alături de echipa chirurgicală, în timpul unei intervenții complexe",
      captionEn: "Dr. Georgescu in the operating room alongside the surgical team during a complex procedure",
    },
    {
      name: "dr-florin-georgescu-interventie-chirurgicala",
      captionRo: "Concentrare maximă în sala de operație — Dr. Georgescu în timpul desfășurării unui act chirurgical",
      captionEn: "Full focus in the operating theatre — Dr. Georgescu performing a surgical procedure",
    },
  ];

  const [fingerprint] = useState(() => {
    let fp = localStorage.getItem('reaction_fp');
    if (!fp) { fp = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('reaction_fp', fp); }
    return fp;
  });
  const [userReactions, setUserReactions] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('user_reactions') || '[]')); } catch { return new Set(); }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (showGdpr) gdprRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showGdpr]);

  useEffect(() => {
    fetch('/api/health').catch(() => {});
    fetch('/api/comments').then(r => r.json()).then(d => Array.isArray(d) && setComments(d)).catch(() => {});
    fetch('/api/questions').then(r => r.json()).then(d => Array.isArray(d) && setQuestions(d)).catch(() => {});
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('adminToken');
    if (!saved) return;
    fetch('/api/admin/verify', { headers: { Authorization: `Bearer ${saved}` } })
      .then(r => r.json())
      .then(d => { if (d.valid) setAdminToken(saved); else localStorage.removeItem('adminToken'); })
      .catch(() => localStorage.removeItem('adminToken'));
  }, []);

  const liverIcon = (
    <svg width="44" height="44" viewBox="0 0 54 54" fill="none" aria-hidden="true">
      {/* Pancreas — elongated hook shape, orange, clearly below liver */}
      <path
        d="M5,44 C7,39 14,37 22,37 C30,37 40,39 48,35 C50,38 49,43 43,45 C34,48 14,48 5,44Z"
        fill="#e67e22"
      />
      <path
        d="M12,38 C20,36 32,37 42,39 C34,37 21,37 12,38Z"
        fill="rgba(255,215,130,0.50)"
      />
      {/* Liver — classic 2-lobe silhouette */}
      <path
        d="M3,23 C2,13 5,5 13,4 C19,3 22,8 22,15
           C22,8 25,3 33,3 C42,3 51,11 51,22
           C51,31 45,37 34,38 C20,38 4,31 3,23Z"
        fill="#c0392b"
      />
      {/* Left lobe — darker for depth */}
      <path
        d="M3,23 C2,13 5,5 13,4 C19,3 22,8 22,15
           C16,18 9,21 6,27 C4,26 3,25 3,23Z"
        fill="#962d22"
      />
      {/* Falciform fissure */}
      <path
        d="M22,15 C22,23 23,31 24,38"
        stroke="rgba(80,10,8,0.20)" strokeWidth="1.5" fill="none" strokeLinecap="round"
      />
      {/* Liver top sheen */}
      <path
        d="M33,4 C42,5 50,12 50,21 C45,13 37,9 31,9 C30,7 31,4 33,4Z"
        fill="rgba(255,200,188,0.42)"
      />
      {/* Cystic duct — thin green line from liver to gallbladder */}
      <path
        d="M35,37 C35,39 35,41 35,43"
        stroke="#1e8449" strokeWidth="1.6" strokeLinecap="round" fill="none"
      />
      {/* Gallbladder — distinct pear/teardrop shape */}
      <path
        d="M31,37 C31,34 33,31 36,31 C39,31 41,34 41,37
           C41,42 39,46 36,46 C33,46 31,42 31,37Z"
        fill="#2ecc71"
      />
      {/* Gallbladder sheen */}
      <ellipse cx="35" cy="34" rx="2" ry="2.5" fill="rgba(255,255,255,0.35)"/>
    </svg>
  );

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = lang === 'ro'
      ? 'Dr. Teodor-Florin Georgescu — Medic Primar Chirurgie Generală București | MD, PhD'
      : 'Dr. Teodor-Florin Georgescu — Senior General Surgeon, Bucharest | MD, PhD';
  }, [lang]);

  const t = {
    ro: {
      name: "Dr. Teodor-Florin Georgescu",
      role: "Medic Primar Chirurgie Generală",
      title: "MD, PhD",
      motto: "Dedicare și excelență pentru fiecare pacient.",
      cta: "Programează consultație",
      
      nav: {
        home: "Acasă",
        about: "Despre mine",
        expertise: "Competență",
        experience: "Experiență",
        gallery: "Galerie",
        comments: "Comentarii",
        qa: "Q&A",
        contact: "Contact",
      },

      comments: {
        title: "Comentarii",
        placeholder: "Scrie un comentariu...",
        submit: "Trimite",
        submitted: "Comentariu trimis. Acesta va fi publicat după ce va fi aprobat de către administrator.",
        pending: "În așteptare de aprobare",
        toggleAdmin: "Mod Admin",
        approve: "Aproba",
        delete: "Șterge",
        edit: "Editează",
        save: "Salvează",
        cancel: "Anulează",
        loadMore: "Afișează mai multe",
        loadLess: "Afișează mai puține",
      },

      qa: {
        title: "Întrebări și Răspunsuri",
        subtitle: "Pune o întrebare și primește răspuns de la Dr. Georgescu",
        placeholder: "Pune o întrebare...",
        submit: "Trimite Întrebarea",
        submitted: "Întrebare trimisă. Aceasta va fi publicată după ce va fi aprobată de către administrator.",
        reply: "Răspunde",
        replyPlaceholder: "Scrie răspunsul...",
        pending: "În așteptare",
        replies: "răspunsuri",
        loadMore: "Afișează mai multe",
      },

      about: {
        title: "Despre mine",
        subtitle: "O carieră dedicată chirurgiei moderne",
        intro: 'Mă numesc Dr. Teodor-Florin Georgescu și sunt medic primar în Chirurgie Generală și Asistent Universitar în cadrul Catedrei de Chirurgie Generală a Spitalului Clinic de Urgență București și UMF „Carol Davila”.',
        bio: "Cu o experiență de peste 12 ani în chirurgia minim invazivă, chirurgia oncologică și chirurgia de urgență, cu o gamă largă de patologii abordate, activitatea mea îmbină practica medicală de vârf cu implicarea academică și cercetarea științifică.",
      },

      credentials: {
        title: "Experiență & Educație",
        subtitle: "Un profil de referință în practica academică și clinică",
        items: [
          { year: "2014 – 2019", title: "Medic Rezident – Chirurgie Generală", institution: "Spitalul Clinic de Urgență București — Floreasca" },
          { year: "2016 – prezent", title: "Asistent Universitar", institution: 'Catedra de Chirurgie Generală, UMF „Carol Davila", București' },
          { year: "2020", title: "Doctor în Medicină (PhD)", institution: 'UMF „Carol Davila", București — teză privind diagnosticul molecular și prognosticul neoplasmelor rectale' },
          { year: "2020 – 2025", title: "Medic Specialist Chirurgie Generală", institution: ["Spitalul OncoFort București / Clinica de diagnostic Gral Medical", "Spitalul MedLife Lotus Ploiești", "Spitalul Clinic de Urgență București — Floreasca"] },
          { year: "din 2025", title: "Medic Primar Chirurgie Generală", institution: ["Spitalul OncoFort București / Clinica de diagnostic Gral Medical", "Spitalul MedLife Lotus Ploiești", "Spitalul Clinic de Urgență București — Floreasca"] },
        ],
        intl: "Formare profesională internațională (Heidelberg, Germania) și peste 25 de cursuri de perfecționare în chirurgia colorectală, chirurgia peretelui abdominal, urgențe chirurgicale, HPB, tehnici laparoscopice avansate.",
        academic: {
          title: "Activitate academică și de formare",
          intro: 'În calitate de cadru universitar UMF „Carol Davila", sunt implicat în:',
          items: [
            "Predarea cursurilor de chirurgie generală (anul IV și V) – module română și engleză",
            "Coordonarea lucrărilor de licență",
            "Organizarea workshopurilor practice pentru studenți și tineri medici («Elementary Skills in Laparoscopy», «Intestinal Anastomoses»)",
            "Participarea în comisii de examen (licență, rezidențiat, examene de specialitate și competențe)",
          ],
        },
        research: {
          title: "Cercetare științifică și publicații",
          intro: "Activitate științifică extinsă:",
          items: [
            "Peste 60 de prezentări la congrese naționale și internaționale",
            "Peste 25 de articole publicate în reviste indexate ISI/PubMed",
            "Participare în proiecte doctorale și postdoctorale privind cancerul colorectal, biologia tumorală, imunohistochimia și microbiota intestinală",
            "Colaborări în lucrări despre traumă, chirurgia pacienților diabetici, complicații ale anastomozelor, patologia pancreatică, rectală, colorectală și oncologică",
            "Contribuții la 4 volume academice și manuale de chirurgie",
          ],
        },
        conferences: {
          title: "Participare la congrese și premii",
          items: [
            "Participant activ la Congresele Naționale de Chirurgie",
            "Participare la conferințe internaționale (EAES, ECTES, IASGO)",
            "Premii pentru prezentări la conferințe și congrese (CNC 2015)",
          ],
        },
        philosophy: {
          title: "Filosofia mea profesională",
          subtitle: "Principii care ghidează practica mea medicală",
          items: [
            { title: "Diagnostic corect", desc: "Abordare personalizată, adaptată fiecărui pacient în parte" },
            { title: "Abord minim invaziv", desc: "Tehnici laparoscopice avansate ori de câte ori este posibil" },
            { title: "Siguranța pacientului", desc: "Reducerea complicațiilor și recuperare postoperatorie rapidă" },
            { title: "Colaborare multidisciplinară", desc: "Rezultate optime prin cooperare în echipă" },
            { title: "Învățare continuă", desc: "Integrarea cercetării și noilor tehnici în practica clinică" },
          ],
        },
      },

      expertise: {
        title: "Arii de competență",
        subtitle: "Specialități și intervenții chirurgicale de top",
        areas: [
          { name: "Chirurgie Oncologică", icon: "🔬", desc: [
            "Cancer de colon",
            "Cancer de rect",
            "Cancer gastric",
            "Cancer de sân",
            "Cancer esofagian",
            "Cancer hepato-biliar și pancreatic",
            "Neoplasme colorectale",
            "Urgențe colorectale",
            "Tehnici minim invazive oncologice",
            "Factori prognostici și tratamente multimodale",
          ]},
          { name: "Chirurgia Peretelui Abdominal", icon: "⚙️", desc: [
            "Hernie hiatală",
            "Hernie inghinală",
            "Hernie ombilicală",
            "Hernie epigastrică",
            "Hernie femurală",
            "Eventrație",
            "Abord deschis sau laparoscopic",
            "Materiale protetice moderne",
          ]},
          { name: "Chirurgia Hepato-Biliară și Pancreatică", icon: liverIcon, desc: [
            "Litiază veziculară",
            "Chist hidatic hepatic",
            "Tumori hepatice benigne",
            "Chisturi pancreatice",
            "Tumori pancreatice",
            "Pancreatită acută și cronică",
          ]},
          { name: "Chirurgie Proctologică", icon: "⚕️", desc: [
            "Hemoroizi",
            "Fisuri anale",
            "Abcese perianale",
            "Flegmoane ischiorectale",
            "Fistule perianale",
            "Chist / abces / fistule pilonidale",
          ]},
          { name: "Chirurgie Metabolică / Funcțională", icon: "⚡", desc: [
            "Chirurgie bariatrică (sleeve gastrectomy)",
            "Rezecții intestinale pentru malabsorbție",
          ]},
          { name: "Alte Intervenții", icon: "🏥", desc: [
            "Histerectomie",
            "Ovariectomie",
            "Chistectomii",
            "Evidare ganglionară interaortocavă",
            "Excizii tumorale",
            "Afecțiuni benigne ale tubului digestiv",
          ]},
        ],
      },

      contact: {
        title: "Contact",
        subtitle: "Programează-ți consultația astazi",
        phone: "+40 (0) 745 161 261",
        email: "florin.georgescu1@yahoo.com",
        hours: "Luni - Vineri: 8:00 - 18:00",
      },

      gdpr: {
        showLabel: "Afișează GDPR",
        hideLabel: "Ascunde GDPR",
        title: "Protecția Datelor (GDPR)",
        subtitle: "Cum colectăm, folosim și protejăm datele dumneavoastră",
        lastUpdated: "Ultima actualizare: august 2026",
        sections: [
          {
            heading: "Operatorul de date",
            body: "Dr. Teodor-Florin Georgescu, cu activitate profesională la Spitalul Clinic de Urgență București (Floreasca), este operatorul de date cu caracter personal pentru acest site. Pentru orice întrebare privind datele dumneavoastră, ne puteți contacta la florin.georgescu1@yahoo.com sau +40 745 161 261.",
          },
          {
            heading: "Ce date colectăm",
            body: [
              "Numele (opțional) și textul mesajului, atunci când trimiteți un comentariu sau o întrebare",
              "Un identificator generat aleatoriu, stocat local în browser (localStorage), folosit exclusiv pentru a preveni reacții duplicate (like / heart / unlike)",
              "Date tehnice standard (adresă IP, marcă temporală) colectate temporar de infrastructura de găzduire, în scopuri de securitate și limitare a numărului de cereri",
            ],
            note: "Nu colectăm date medicale prin intermediul acestui site și nu folosim cookie-uri de urmărire sau publicitate.",
          },
          {
            heading: "Scopul și temeiul legal al prelucrării",
            body: [
              "Publicarea comentariilor și a răspunsurilor la întrebări, după moderare — temei legal: consimțământul dumneavoastră (art. 6 alin. (1) lit. a GDPR), acordat prin trimiterea formularului",
              "Prevenirea abuzurilor și a reacțiilor duplicate — temei legal: interesul legitim (art. 6 alin. (1) lit. f GDPR)",
            ],
          },
          {
            heading: "Stocarea și durata de păstrare",
            body: "Comentariile, întrebările și răspunsurile sunt păstrate într-o bază de date securizată până când sunt șterse de administratorul site-ului. Nu există un termen automat de expirare; puteți solicita oricând ștergerea unui mesaj trimis de dumneavoastră.",
          },
          {
            heading: "Cookie-uri și stocare locală",
            body: "Site-ul nu folosește cookie-uri de urmărire sau publicitate. Este folosit exclusiv localStorage-ul browserului dumneavoastră, pentru a reține limba preferată și un identificator anonim folosit la reacții, fără a fi transmis către terți.",
          },
          {
            heading: "Divulgarea către terți",
            body: "Nu vindem și nu partajăm datele dumneavoastră cu terți în scopuri de marketing. Datele pot fi accesate doar de furnizorul de găzduire și de baza de date, strict în scopul funcționării site-ului.",
          },
          {
            heading: "Drepturile dumneavoastră",
            body: [
              "Acces la datele prelucrate",
              "Rectificarea datelor inexacte",
              "Ștergerea datelor („dreptul de a fi uitat”)",
              "Restricționarea prelucrării",
              "Opoziție față de prelucrare",
              "Portabilitatea datelor",
              "Depunerea unei plângeri la Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP), www.dataprotection.ro",
            ],
            note: "Conform Regulamentului (UE) 2016/679 (GDPR), aceste drepturi pot fi exercitate oricând prin contactarea noastră la datele afișate mai sus.",
          },
          {
            heading: "Securitate",
            body: "Comunicarea cu site-ul este criptată (HTTPS). Accesul administrativ este protejat prin parolă și autentificare prin token, iar cererile către server sunt limitate ca număr pentru prevenirea abuzurilor.",
          },
          {
            heading: "Modificări ale acestei politici",
            body: "Această politică poate fi actualizată periodic. Data ultimei actualizări este afișată la începutul paginii.",
          },
        ],
      },
    },

    en: {
      name: "Dr. Teodor-Florin Georgescu",
      role: "Senior General Surgeon",
      title: "MD, PhD",
      motto: "Dedication and excellence for every patient.",
      cta: "Book consultation",
      
      nav: {
        home: "Home",
        about: "About",
        expertise: "Expertise",
        experience: "Experience",
        gallery: "Gallery",
        comments: "Comments",
        qa: "Q&A",
        contact: "Contact",
      },

      comments: {
        title: "Comments",
        placeholder: "Write a comment...",
        submit: "Post",
        submitted: "Comment submitted. It will be published after being approved by the administrator.",
        pending: "Pending approval",
        toggleAdmin: "Admin Mode",
        approve: "Approve",
        delete: "Delete",
        edit: "Edit",
        save: "Save",
        cancel: "Cancel",
        loadMore: "Show more",
        loadLess: "Show fewer",
      },

      qa: {
        title: "Questions & Answers",
        subtitle: "Ask a question and get a response from Dr. Georgescu",
        placeholder: "Ask a question...",
        submit: "Submit Question",
        submitted: "Question submitted. It will be published after being approved by the administrator.",
        reply: "Reply",
        replyPlaceholder: "Write a reply...",
        pending: "Pending",
        replies: "replies",
        loadMore: "Show more",
      },

      about: {
        title: "About Me",
        subtitle: "A career dedicated to modern surgery",
        intro: "I am Dr. Teodor-Florin Georgescu, Senior General Surgeon and University Assistant at the Department of General Surgery, Emergency Clinical Hospital Bucharest and UMF 'Carol Davila'.",
        bio: "With over 12 years of experience in minimally invasive surgery, oncological surgery, and emergency surgery, covering a wide range of pathologies, my practice combines cutting-edge clinical care with academic involvement and scientific research.",
      },

      credentials: {
        title: "Experience & Education",
        subtitle: "A reference profile in academic and clinical practice",
        items: [
          { year: "2014 – 2019", title: "Surgical Resident – General Surgery", institution: "Emergency Clinical Hospital Bucharest — Floreasca" },
          { year: "2016 – present", title: "University Assistant", institution: "Dept. of General Surgery, UMF 'Carol Davila', Bucharest" },
          { year: "2020", title: "Doctor of Medicine (PhD)", institution: "UMF 'Carol Davila', Bucharest — thesis on molecular diagnosis and prognosis of rectal neoplasms" },
          { year: "2020 – 2025", title: "General Surgery Specialist", institution: ["OncoFort Hospital Bucharest / Gral Medical Diagnostic Clinic", "MedLife Lotus Hospital Ploiești", "Emergency Clinical Hospital Bucharest — Floreasca"] },
          { year: "since 2025", title: "Senior General Surgeon (Medic Primar)", institution: ["OncoFort Hospital Bucharest / Gral Medical Diagnostic Clinic", "MedLife Lotus Hospital Ploiești", "Emergency Clinical Hospital Bucharest — Floreasca"] },
        ],
        intl: "International professional training (Heidelberg, Germany) and over 25 advanced courses in colorectal surgery, abdominal wall surgery, surgical emergencies, HPB, and advanced laparoscopic techniques.",
        academic: {
          title: "Academic & Training Activity",
          intro: "As a faculty member at UMF 'Carol Davila', I am involved in:",
          items: [
            "Teaching general surgery courses (4th and 5th year) – Romanian and English modules",
            "Supervising undergraduate dissertations",
            "Organising practical workshops for students and junior doctors ('Elementary Skills in Laparoscopy', 'Intestinal Anastomoses')",
            "Participation in examination committees (graduation, residency, specialty and competency exams)",
          ],
        },
        research: {
          title: "Scientific Research & Publications",
          intro: "Extensive scientific record:",
          items: [
            "Over 60 presentations at national and international congresses",
            "Over 25 articles published in ISI/PubMed-indexed journals",
            "Participation in doctoral and post-doctoral projects on colorectal cancer, tumour biology, immunohistochemistry, and intestinal microbiota",
            "Contributions to works on trauma, diabetic patients surgery, anastomotic complications, pancreatic, rectal, colorectal and oncological pathology",
            "Contributions to 4 academic volumes and surgical textbooks",
          ],
        },
        conferences: {
          title: "Congresses & Awards",
          items: [
            "Active participant at National Surgery Congresses",
            "Participation at international conferences (EAES, ECTES, IASGO)",
            "Awards for presentations at conferences and congresses (CNC 2015)",
          ],
        },
        philosophy: {
          title: "My Professional Philosophy",
          subtitle: "Principles guiding my medical practice",
          items: [
            { title: "Accurate Diagnosis", desc: "Personalised approach tailored to each individual patient" },
            { title: "Minimally Invasive", desc: "Advanced laparoscopic techniques whenever possible" },
            { title: "Patient Safety", desc: "Complication reduction and rapid postoperative recovery" },
            { title: "Multidisciplinary", desc: "Optimal outcomes through team collaboration" },
            { title: "Continuous Learning", desc: "Integrating research and new techniques into clinical practice" },
          ],
        },
      },

      expertise: {
        title: "Areas of Expertise",
        subtitle: "Specialized and cutting-edge surgical procedures",
        areas: [
          { name: "Oncological Surgery", icon: "🔬", desc: [
            "Colon cancer",
            "Rectal cancer",
            "Gastric cancer",
            "Breast cancer",
            "Oesophageal cancer",
            "Hepatobiliary and pancreatic cancer",
            "Colorectal neoplasms",
            "Colorectal emergencies",
            "Minimally invasive oncological techniques",
            "Prognostic factors and multimodal treatment",
          ]},
          { name: "Abdominal Wall Surgery", icon: "⚙️", desc: [
            "Hiatal hernia",
            "Inguinal hernia",
            "Umbilical hernia",
            "Epigastric hernia",
            "Femoral hernia",
            "Incisional hernia (eventration)",
            "Open and laparoscopic approach",
            "Modern prosthetic materials",
          ]},
          { name: "Hepato-Biliary & Pancreatic Surgery", icon: liverIcon, desc: [
            "Gallstone disease",
            "Hepatic hydatid cyst",
            "Benign liver tumours",
            "Pancreatic cysts",
            "Pancreatic tumours",
            "Acute and chronic pancreatitis",
          ]},
          { name: "Proctological Surgery", icon: "⚕️", desc: [
            "Haemorrhoids",
            "Anal fissures",
            "Perianal abscesses",
            "Ischiorectal cellulitis",
            "Perianal fistulas",
            "Pilonidal cyst / abscess / fistula",
          ]},
          { name: "Metabolic / Functional Surgery", icon: "⚡", desc: [
            "Bariatric surgery (sleeve gastrectomy)",
            "Intestinal resections for malabsorption",
          ]},
          { name: "Other Procedures", icon: "🏥", desc: [
            "Hysterectomy",
            "Oophorectomy",
            "Cystectomies",
            "Interaortocaval lymph node dissection",
            "Tumour excisions",
            "Benign gastrointestinal pathology",
          ]},
        ],
      },

      contact: {
        title: "Contact",
        subtitle: "Schedule your consultation today",
        phone: "+40 (0) 745 161 261",
        email: "florin.georgescu1@yahoo.com",
        hours: "Monday - Friday: 8:00 - 18:00",
      },

      gdpr: {
        showLabel: "Show GDPR",
        hideLabel: "Hide GDPR",
        title: "Data Protection (GDPR)",
        subtitle: "How we collect, use, and protect your data",
        lastUpdated: "Last updated: August 2026",
        sections: [
          {
            heading: "Data controller",
            body: "Dr. Teodor-Florin Georgescu, practicing at Spitalul Clinic de Urgență București (Floreasca), is the data controller for this website. For any question regarding your data, you can reach us at florin.georgescu1@yahoo.com or +40 745 161 261.",
          },
          {
            heading: "What data we collect",
            body: [
              "Your name (optional) and message text, when you submit a comment or a question",
              "A randomly generated identifier stored locally in your browser (localStorage), used only to prevent duplicate reactions (like / heart / unlike)",
              "Standard technical data (IP address, timestamp) temporarily collected by the hosting infrastructure, for security and request-rate limiting purposes",
            ],
            note: "We do not collect medical data through this website, and we do not use tracking or advertising cookies.",
          },
          {
            heading: "Purpose and legal basis for processing",
            body: [
              "Publishing comments and answers to questions, after moderation — legal basis: your consent (Art. 6(1)(a) GDPR), given by submitting the form",
              "Preventing abuse and duplicate reactions — legal basis: legitimate interest (Art. 6(1)(f) GDPR)",
            ],
          },
          {
            heading: "Storage and retention period",
            body: "Comments, questions, and replies are kept in a secured database until removed by the site administrator. There is no automatic expiry period; you may request the deletion of a message you submitted at any time.",
          },
          {
            heading: "Cookies and local storage",
            body: "This site does not use tracking or advertising cookies. It only uses your browser's localStorage, to remember your preferred language and an anonymous identifier used for reactions, without sending it to any third party.",
          },
          {
            heading: "Disclosure to third parties",
            body: "We do not sell or share your data with third parties for marketing purposes. Data can only be accessed by our hosting provider and database, strictly for the purpose of operating the website.",
          },
          {
            heading: "Your rights",
            body: [
              "Access to the data being processed",
              "Rectification of inaccurate data",
              "Erasure of data (\"the right to be forgotten\")",
              "Restriction of processing",
              "Objection to processing",
              "Data portability",
              "Lodging a complaint with the Romanian National Supervisory Authority for Personal Data Processing (ANSPDCP), www.dataprotection.ro, or your local supervisory authority",
            ],
            note: "Under Regulation (EU) 2016/679 (GDPR), these rights can be exercised at any time by contacting us using the details above.",
          },
          {
            heading: "Security",
            body: "Communication with the site is encrypted (HTTPS). Administrative access is protected by a password and token-based authentication, and requests to the server are rate-limited to prevent abuse.",
          },
          {
            heading: "Changes to this policy",
            body: "This policy may be updated periodically. The date of the last update is shown at the top of this page.",
          },
        ],
      },
    },
  }[lang];

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString(lang === "ro" ? "ro-RO" : "en-US", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const showToast = (message) => {
    const id = Date.now();
    setToast({ id, message });
    setTimeout(() => setToast(t => t?.id === id ? null : t), 5000);
  };

  const showSubmissionAlert = (message) => {
    setSubmissionAlert(message);
    setTimeout(() => setSubmissionAlert(null), 15000);
  };

  // Comments handlers
  const handleAddComment = async () => {
    const now = Date.now();
    if (now - commentLastPostTime < 15000) {
      showToast(lang === "ro" ? "Asteaptă înainte să postezi din nou" : "Please wait before posting again");
      return;
    }
    if (!commentUsername.trim()) {
      showToast(lang === "ro" ? "Numele este obligatoriu" : "Name is required");
      return;
    }
    if (!commentInput.trim()) return;

    const name = commentUsername.trim();
    try {
      const response = await fetch("/api/comments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: name, text: commentInput }),
      });
      if (response.ok) {
        const newComment = await response.json();
        setComments([newComment, ...comments]);
        setCommentInput("");
        setCommentUsername("");
        setCommentLastPostTime(now);
        showSubmissionAlert(t.comments.submitted);
        return;
      }
    } catch (_) {}
    // offline fallback
    setComments([{
      id: Date.now(),
      username: name,
      text: commentInput,
      approved: false,
      created_at: new Date().toISOString(),
    }, ...comments]);
    setCommentInput("");
    setCommentUsername("");
    setCommentLastPostTime(now);
    showSubmissionAlert(t.comments.submitted);
  };

  const handleAdminLogin = async () => {
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setAdminToken(data.token);
        localStorage.setItem('adminToken', data.token);
        setShowAdminLogin(false);
        setAdminPassword("");
        setAdminError("");
      } else {
        setAdminError(data.error || (lang === "ro" ? "Parolă incorectă" : "Incorrect password"));
      }
    } catch {
      setAdminError(lang === "ro" ? "Eroare de conexiune" : "Connection error");
    }
  };

  const handleAdminLogout = () => {
    setAdminToken(null);
    localStorage.removeItem('adminToken');
  };

  const authHeader = () => ({ Authorization: `Bearer ${adminToken}` });

  const approveComment = async (id) => {
    try {
      const res = await fetch(`/api/comments/${id}/approve`, { method: "PATCH", headers: authHeader() });
      if (res.status === 401) { handleAdminLogout(); return; }
    } catch (_) {}
    setComments(prev => prev.map(c => c.id === id ? { ...c, approved: true } : c));
    showToast(lang === "ro" ? "Comentariu aprobat!" : "Comment approved!");
  };

  const deleteComment = async (id) => {
    if (!window.confirm(lang === "ro" ? "Ești sigur că vrei să ștergi acest comentariu?" : "Are you sure you want to delete this comment?")) return;
    try {
      const res = await fetch(`/api/comments/${id}`, { method: "DELETE", headers: authHeader() });
      if (res.status === 401) { handleAdminLogout(); return; }
    } catch (_) {}
    setComments(prev => prev.filter(c => c.id !== id));
  };

  const saveEditComment = async (id, text) => {
    try {
      const res = await fetch(`/api/comments/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ text }),
      });
      if (res.status === 401) { handleAdminLogout(); return; }
      if (res.ok) {
        const updated = await res.json();
        setComments(prev => prev.map(c => c.id === id ? { ...c, text: updated.text } : c));
        showToast(lang === 'ro' ? 'Comentariu editat cu succes!' : 'Comment edited successfully!');
      } else {
        showToast(lang === 'ro' ? 'Eroare la editare.' : 'Edit failed.');
      }
    } catch (_) {
      showToast(lang === 'ro' ? 'Eroare la editare.' : 'Edit failed.');
    }
    setEditingItem(null);
  };

  const saveEditQuestion = async (id, text) => {
    try {
      const res = await fetch(`/api/questions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ text }),
      });
      if (res.status === 401) { handleAdminLogout(); return; }
      if (res.ok) {
        const updated = await res.json();
        setQuestions(prev => prev.map(q => q.id === id ? { ...q, text: updated.text } : q));
        showToast(lang === 'ro' ? 'Întrebare editată cu succes!' : 'Question edited successfully!');
      } else {
        showToast(lang === 'ro' ? 'Eroare la editare.' : 'Edit failed.');
      }
    } catch (_) {
      showToast(lang === 'ro' ? 'Eroare la editare.' : 'Edit failed.');
    }
    setEditingItem(null);
  };

  const saveEditReply = async (qid, rid, text) => {
    try {
      const res = await fetch(`/api/questions/${qid}/replies/${rid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ text }),
      });
      if (res.status === 401) { handleAdminLogout(); return; }
      if (res.ok) {
        const updated = await res.json();
        setQuestions(prev => prev.map(q => q.id === qid
          ? { ...q, replies: q.replies.map(r => r.id === rid ? { ...r, text: updated.text } : r) }
          : q
        ));
        showToast(lang === 'ro' ? 'Răspuns editat cu succes!' : 'Reply edited successfully!');
      } else {
        showToast(lang === 'ro' ? 'Eroare la editare.' : 'Edit failed.');
      }
    } catch (_) {
      showToast(lang === 'ro' ? 'Eroare la editare.' : 'Edit failed.');
    }
    setEditingItem(null);
  };

  const handleReact = async (entityType, entityId, reactionType) => {
    const key = `${entityType}_${entityId}_${reactionType}`;
    const isAdding = !userReactions.has(key);
    const countKey = reactionType === 'unlike' ? 'unlikes' : reactionType + 's';
    const delta = isAdding ? 1 : -1;

    // Optimistic update — works immediately regardless of DB state
    const next = new Set(userReactions);
    isAdding ? next.add(key) : next.delete(key);
    setUserReactions(next);
    localStorage.setItem('user_reactions', JSON.stringify([...next]));
    const applyDelta = (e) => ({ ...e, [countKey]: Math.max(0, (e[countKey] || 0) + delta) });
    if (entityType === 'comment') {
      setComments(prev => prev.map(c => c.id === entityId ? applyDelta(c) : c));
    } else if (entityType === 'question') {
      setQuestions(prev => prev.map(q => q.id === entityId ? applyDelta(q) : q));
    } else {
      setQuestions(prev => prev.map(q => ({ ...q, replies: q.replies.map(r => r.id === entityId ? applyDelta(r) : r) })));
    }

    // Best-effort server sync — if DB is up, reconcile counts
    try {
      const res = await fetch('/api/react', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_type: entityType, entity_id: entityId, reaction_type: reactionType, fingerprint }),
      });
      if (res.ok) {
        const data = await res.json();
        const patch = { likes: data.likes, hearts: data.hearts, unlikes: data.unlikes };
        if (entityType === 'comment') setComments(prev => prev.map(c => c.id === entityId ? { ...c, ...patch } : c));
        else if (entityType === 'question') setQuestions(prev => prev.map(q => q.id === entityId ? { ...q, ...patch } : q));
        else setQuestions(prev => prev.map(q => ({ ...q, replies: q.replies.map(r => r.id === entityId ? { ...r, ...patch } : r) })));
      }
    } catch (_) {}
  };

  // Q&A handlers
  const handleAddQuestion = async () => {
    const now = Date.now();
    if (now - questionLastPostTime < 15000) {
      showToast(lang === "ro" ? "Asteaptă înainte să postezi din nou" : "Please wait before posting again");
      return;
    }
    if (!questionUsername.trim()) {
      showToast(lang === "ro" ? "Numele este obligatoriu" : "Name is required");
      return;
    }
    if (!questionInput.trim()) return;

    const name = questionUsername.trim();
    try {
      const response = await fetch("/api/questions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: name, text: questionInput }),
      });
      if (response.ok) {
        const newQuestion = await response.json();
        setQuestions([newQuestion, ...questions]);
        setQuestionInput("");
        setQuestionUsername("");
        setQuestionLastPostTime(now);
        showSubmissionAlert(t.qa.submitted);
        return;
      }
    } catch (_) {}
    // offline fallback
    setQuestions([{
      id: Date.now(),
      username: name,
      text: questionInput,
      approved: false,
      created_at: new Date().toISOString(),
      replies: [],
    }, ...questions]);
    setQuestionInput("");
    setQuestionUsername("");
    setQuestionLastPostTime(now);
    showSubmissionAlert(t.qa.submitted);
  };

  const handleAddReply = async (questionId) => {
    const reply = replyInput[questionId];
    if (!reply || !reply.trim()) return;

    try {
      const response = await fetch(`/api/questions/${questionId}/replies`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ username: "Dr. Georgescu", text: reply }),
      });
      if (response.ok) {
        const newReply = await response.json();
        setQuestions(questions.map(q =>
          q.id === questionId ? { ...q, replies: [...q.replies, newReply] } : q
        ));
        setReplyInput({ ...replyInput, [questionId]: "" });
        showToast(lang === "ro" ? "Răspuns trimis!" : "Reply posted!");
        return;
      }
    } catch (_) {}
    // offline fallback
    setQuestions(questions.map(q =>
      q.id === questionId
        ? { ...q, replies: [...q.replies, {
            id: Date.now(),
            username: "Dr. Georgescu",
            text: reply,
            approved: false,
            created_at: new Date().toISOString(),
          }] }
        : q
    ));
    setReplyInput({ ...replyInput, [questionId]: "" });
    showToast(lang === "ro" ? "Răspuns trimis!" : "Reply posted!");
  };

  const approveReply = async (questionId, replyId) => {
    try {
      const res = await fetch(`/api/questions/${questionId}/replies/${replyId}/approve`, { method: "PATCH", headers: authHeader() });
      if (res.status === 401) { handleAdminLogout(); return; }
    } catch (_) {}
    setQuestions(prev => prev.map(q =>
      q.id === questionId ? { ...q, replies: q.replies.map(r => r.id === replyId ? { ...r, approved: true } : r) } : q
    ));
    showToast(lang === "ro" ? "Răspuns aprobat!" : "Reply approved!");
  };

  const deleteReply = async (questionId, replyId) => {
    if (!window.confirm(lang === "ro" ? "Ești sigur că vrei să ștergi acest răspuns?" : "Are you sure you want to delete this reply?")) return;
    try {
      const res = await fetch(`/api/questions/${questionId}/replies/${replyId}`, { method: "DELETE", headers: authHeader() });
      if (res.status === 401) { handleAdminLogout(); return; }
    } catch (_) {}
    setQuestions(prev => prev.map(q =>
      q.id === questionId ? { ...q, replies: q.replies.filter(r => r.id !== replyId) } : q
    ));
  };

  const approveQuestion = async (id) => {
    try {
      const res = await fetch(`/api/questions/${id}/approve`, { method: "PATCH", headers: authHeader() });
      if (res.status === 401) { handleAdminLogout(); return; }
    } catch (_) {}
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, approved: true } : q));
    showToast(lang === "ro" ? "Întrebare aprobată!" : "Question approved!");
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm(lang === "ro" ? "Ești sigur că vrei să ștergi această întrebare și toate răspunsurile ei?" : "Are you sure you want to delete this question and all its replies?")) return;
    try {
      const res = await fetch(`/api/questions/${id}`, { method: "DELETE", headers: authHeader() });
      if (res.status === 401) { handleAdminLogout(); return; }
    } catch (_) {}
    setQuestions(prev => prev.filter(q => q.id !== id));
  };

  const iconStyle = {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    background: "#4fc3d9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#1a1a1a",
    transition: "all 0.3s",
    textDecoration: "none",
  };

  const filteredComments = comments.filter(c => c.approved || isAdmin);
  const filteredQuestions = questions.filter(q => q.approved || isAdmin);

  return (
    <div style={{ fontFamily: "'Lora', 'Georgia', serif", color: "#2c2c2c", background: "#fafaf8" }}>
      {/* ===== HEADER ===== */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: isScrolled ? "rgba(250, 250, 248, 0.95)" : "rgba(250, 250, 248, 0.7)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          borderBottom: isScrolled ? "1px solid #e0dcd5" : "none",
          transition: "all 0.3s ease",
          height: "70px",
        }}
      >
        <div className="header-inner" style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 40px", height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <a href="#home" onClick={() => setActiveNav("home")} style={{ flexShrink: 0, display: "block", lineHeight: 0 }}>
            <img src="/dr-florin-georgescu-logo-120.webp" srcSet="/dr-florin-georgescu-logo-120.webp 120w, /dr-florin-georgescu-logo-300.webp 300w" sizes="54px" width="54" height="54" style={{ height: "54px", width: "auto", display: "block", borderRadius: "2px", cursor: "pointer" }} alt={lang === "ro" ? "Dr. Teodor-Florin Georgescu — logo, acasă" : "Dr. Teodor-Florin Georgescu — logo, home"} />
          </a>

          <nav style={{ display: "flex", gap: "35px", listStyle: "none" }} className="desktop-nav">
            {["home", "about", "expertise", "experience", "gallery", "comments", "qa", "contact"].map((section) => (
              <a
                key={section}
                href={`#${section}`}
                onClick={() => setActiveNav(section)}
                style={{
                  textDecoration: "none",
                  color: activeNav === section ? "#167485" : "#2c2c2c",
                  fontSize: "13px",
                  fontWeight: "500",
                  letterSpacing: "0.3px",
                  textTransform: "uppercase",
                  transition: "color 0.3s ease",
                  borderBottom: activeNav === section ? "2px solid #4fc3d9" : "2px solid transparent",
                  paddingBottom: "2px",
                }}
                onMouseEnter={(e) => { e.target.style.color = "#4fc3d9"; }}
                onMouseLeave={(e) => { if (activeNav !== section) e.target.style.color = "#2c2c2c"; }}
              >
                {t.nav[section]}
              </a>
            ))}
          </nav>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {isAdmin && (
              <button
                onClick={handleAdminLogout}
                style={{ background: "#e53935", color: "white", border: "none", padding: "5px 12px", borderRadius: "4px", cursor: "pointer", fontSize: "11px", fontWeight: "600", letterSpacing: "0.5px", textTransform: "uppercase" }}
              >
                {lang === "ro" ? "Ieși Admin" : "Logout Admin"}
              </button>
            )}
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={() => setLang("ro")} style={{ background: "none", border: "none", fontSize: "12px", fontWeight: lang === "ro" ? "700" : "400", color: lang === "ro" ? "#167485" : "#6b6b6b", cursor: "pointer", transition: "color 0.3s", textTransform: "uppercase" }}>RO</button>
              <span style={{ color: "#ddd" }}>|</span>
              <button onClick={() => setLang("en")} style={{ background: "none", border: "none", fontSize: "12px", fontWeight: lang === "en" ? "700" : "400", color: lang === "en" ? "#167485" : "#6b6b6b", cursor: "pointer", transition: "color 0.3s", textTransform: "uppercase" }}>EN</button>
            </div>
            <button className={`hamburger-btn${menuOpen ? " open" : ""}`} onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
              <span /><span /><span />
            </button>
          </div>
        </div>

        {/* Mobile nav overlay */}
        <div className={`mobile-nav-overlay${menuOpen ? " open" : ""}`}>
          {["home", "about", "expertise", "experience", "gallery", "comments", "qa", "contact"].map((section) => (
            <a
              key={section}
              href={`#${section}`}
              className={activeNav === section ? "active" : ""}
              onClick={() => { setActiveNav(section); setMenuOpen(false); }}
            >
              {t.nav[section]}
            </a>
          ))}
        </div>
      </header>

      <main>

      {/* ===== HERO ===== */}
      <section id="home" className="hero-section" style={{ height: "100vh", display: "flex", alignItems: "center", background: "linear-gradient(135deg, #fafaf8 0%, #f5f3f0 100%)", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-50%", right: "-10%", width: "600px", height: "600px", background: "radial-gradient(circle, rgba(79, 195, 217, 0.08) 0%, transparent 70%)", borderRadius: "50%", pointerEvents: "none" }} />

        <div className="hero-inner" style={{ display: "flex", width: "100%", maxWidth: "1200px", margin: "0 auto", position: "relative", zIndex: 2, alignSelf: "stretch" }}>
          <div className="hero-text" style={{ flex: 1, padding: "80px 60px 80px 80px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ fontSize: "13px", fontWeight: "600", letterSpacing: "2px", textTransform: "uppercase", color: "#167485", marginBottom: "20px" }}>
              {lang === "ro" ? "Chirurgie Generală de top" : "CUTTING-EDGE GENERAL SURGERY"}
            </div>

            <h1 style={{ fontSize: "56px", fontWeight: "400", lineHeight: "1.1", marginBottom: "12px", color: "#1a1a1a" }}>
              {t.name}
            </h1>

            <p style={{ fontSize: "18px", color: "#666", fontWeight: "400", marginBottom: "10px", letterSpacing: "0.5px" }}>
              {t.role}, {t.title}
            </p>

            <p style={{ fontSize: "16px", color: "#6b6b6b", fontStyle: "italic", marginTop: "24px", marginBottom: "40px", maxWidth: "500px", lineHeight: "1.6" }}>
              "{t.motto}"
            </p>

            {/* CENTERED CTA BUTTON */}
            <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <a
                href="https://wa.me/40738749466"
                style={{
                  display: "inline-block",
                  background: "#1a1a1a",
                  color: "white",
                  padding: "16px 40px",
                  fontSize: "13px",
                  fontWeight: "600",
                  textDecoration: "none",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  borderRadius: "0",
                  transition: "all 0.3s ease",
                  border: "2px solid #1a1a1a",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => { e.target.style.background = "transparent"; e.target.style.color = "#1a1a1a"; }}
                onMouseLeave={(e) => { e.target.style.background = "#1a1a1a"; e.target.style.color = "white"; }}
              >
                {t.cta} →
              </a>
            </div>
          </div>

          <div className="hero-img" style={{ flex: "0 0 45%", height: "100%", position: "relative", overflow: "hidden", background: "linear-gradient(135deg, #fafaf8 0%, #f5f3f0 100%)" }}>
            <img src={imgSrc("dr-florin-georgescu-medic-chirurg")} srcSet={imgSrcSet("dr-florin-georgescu-medic-chirurg")} sizes="(max-width: 768px) 100vw, 45vw" width="996" height="1600" fetchpriority="high" alt={lang === "ro" ? "Dr. Teodor-Florin Georgescu — medic primar chirurgie generală, București" : "Dr. Teodor-Florin Georgescu — senior consultant in general surgery, Bucharest"} style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center bottom" }} />
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" className="section-pad" style={{ padding: "100px 40px", background: "white" }}>
        <div className="about-inner" style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", gap: "80px", alignItems: "center" }}>
        <div className="about-photo" style={{ flex: 1, overflow: "hidden" }}>
          <img src={imgSrc("dr-florin-georgescu-logo")} srcSet={imgSrcSet("dr-florin-georgescu-logo")} sizes="(max-width: 768px) 100vw, 560px" width="500" height="500" loading="lazy" decoding="async" alt={lang === "ro" ? "Logo Dr. Teodor-Florin Georgescu — medic chirurg, MD, PhD" : "Dr. Teodor-Florin Georgescu logo — surgeon, MD, PhD"} style={{ width: "100%", height: "auto", borderRadius: "2px", objectFit: "cover" }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "13px", fontWeight: "600", letterSpacing: "2px", textTransform: "uppercase", color: "#167485", marginBottom: "16px" }}>
            {lang === "ro" ? "Experiență și Devotament" : "Experience & Dedication"}
          </div>
          <h2 className="section-title" style={{ fontSize: "42px", fontWeight: "400", marginBottom: "28px", color: "#1a1a1a", lineHeight: "1.2" }}>
            {t.about.title}
          </h2>
          <p style={{ fontSize: "15px", lineHeight: "1.8", color: "#666", marginBottom: "20px" }}>
            {t.about.intro}
          </p>
          <p style={{ fontSize: "15px", lineHeight: "1.8", color: "#666", marginBottom: "32px" }}>
            {t.about.bio}
          </p>
          <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            {[
              { label: lang === "ro" ? "Ani de experiență" : "Years of experience", value: "12+" },
              { label: lang === "ro" ? "Pacienți tratați" : "Patients treated", value: "1000+" },
              { label: lang === "ro" ? "Prezentări la congrese" : "Congress presentations", value: "60+" },
              { label: lang === "ro" ? "Publicații ISI/PubMed" : "ISI/PubMed publications", value: "25+" },
            ].map((stat, i) => (
              <div key={i} style={{ background: "#fafaf8", padding: "16px 20px", borderRadius: "2px", borderLeft: "3px solid #4fc3d9" }}>
                <p style={{ margin: "0 0 4px 0", fontSize: "22px", fontWeight: "600", color: "#1a1a1a" }}>{stat.value}</p>
                <p style={{ margin: "0", fontSize: "12px", color: "#6b6b6b", textTransform: "uppercase", letterSpacing: "0.5px" }}>{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Video links */}
          <div style={{ marginTop: "36px" }}>
            <p style={{ fontSize: "13px", fontWeight: "600", letterSpacing: "2px", textTransform: "uppercase", color: "#167485", marginBottom: "16px" }}>
              {lang === "ro" ? "Urmărește-mă în acțiune" : "Watch me in action"}
            </p>
            <div className="video-links" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {[
                {
                  icon: <FaFacebook size={20} />,
                  label: "Facebook",
                  desc: lang === "ro" ? "Reels chirurgicale" : "Surgical reels",
                  color: "#1877F2",
                  href: "https://www.facebook.com/reel/2609000602602871",
                },
                {
                  icon: <FaYoutube size={20} />,
                  label: "YouTube",
                  desc: lang === "ro" ? "Videoclipuri medicale" : "Medical videos",
                  color: "#FF0000",
                  href: "https://www.youtube.com/watch?v=ChPWAfMVQoU",
                },
                {
                  icon: <FaTiktok size={20} />,
                  label: "TikTok",
                  desc: lang === "ro" ? "Educație medicală" : "Medical education",
                  color: "#010101",
                  href: "https://www.tiktok.com/@dr.florin.georgescu/video/7609283504836447490",
                },
              ].map((v, i) => (
                <a
                  key={i}
                  href={v.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 18px",
                    background: "#fafaf8",
                    borderRadius: "2px",
                    borderLeft: `3px solid ${v.color}`,
                    textDecoration: "none",
                    color: "#1a1a1a",
                    transition: "box-shadow 0.2s",
                    flex: "1 1 140px",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                >
                  <span style={{ color: v.color }}>{v.icon}</span>
                  <div>
                    <p style={{ margin: 0, fontSize: "13px", fontWeight: "600" }}>{v.label}</p>
                    <p style={{ margin: 0, fontSize: "11px", color: "#6b6b6b" }}>{v.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* ===== EXPERTISE ===== */}
      <section id="expertise" className="section-pad" style={{ padding: "100px 40px", background: "white" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "80px" }}>
          <h2 className="section-title" style={{ fontSize: "42px", fontWeight: "400", marginBottom: "16px", color: "#1a1a1a" }}>
            {t.expertise.title}
          </h2>
          <p style={{ fontSize: "16px", color: "#6b6b6b", maxWidth: "600px", margin: "0 auto" }}>
            {t.expertise.subtitle}
          </p>
        </div>
        <div className="expertise-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "32px" }}>
          {t.expertise.areas.map((area, i) => (
            <div key={i} className="expertise-card" style={{ background: "#fafaf8", padding: "40px 32px", borderRadius: "2px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", transition: "all 0.3s ease", cursor: "pointer" }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-4px)"; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "translateY(0)"; }}>
              <div style={{ fontSize: "32px", marginBottom: "16px" }}>{area.icon}</div>
              <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#1a1a1a", marginBottom: "12px" }}>
                {area.name}
              </h3>
              <ul style={{ margin: "0", paddingLeft: "16px" }}>
                {area.desc.map((item, j) => (
                  <li key={j} style={{ fontSize: "13px", color: "#666", lineHeight: "1.7", marginBottom: "2px" }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        </div>
      </section>

      {/* ===== EXPERIENCE ===== */}
      <section id="experience" className="section-pad" style={{ padding: "100px 40px", background: "#fafaf8" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>

          {/* Section heading */}
          <div style={{ textAlign: "center", marginBottom: "80px" }}>
            <div style={{ fontSize: "13px", fontWeight: "600", letterSpacing: "2px", textTransform: "uppercase", color: "#167485", marginBottom: "16px" }}>
              {lang === "ro" ? "Parcurs Medical" : "Medical Journey"}
            </div>
            <h2 className="section-title" style={{ fontSize: "42px", fontWeight: "400", marginBottom: "16px", color: "#1a1a1a" }}>
              {t.credentials.title}
            </h2>
            <p style={{ fontSize: "16px", color: "#6b6b6b", maxWidth: "600px", margin: "0 auto" }}>
              {t.credentials.subtitle}
            </p>
          </div>

          {/* Timeline */}
          <div style={{ position: "relative", maxWidth: "700px", margin: "0 auto 60px" }}>
            <div className="timeline-line" style={{ position: "absolute", left: "130px", top: 0, bottom: 0, width: "1px", background: "#e0dcd5" }} />
            {t.credentials.items.map((item, i) => (
              <div key={i} style={{ display: "flex", marginBottom: "36px", position: "relative", alignItems: "flex-start" }}>
                <div className="timeline-year" style={{ width: "130px", flexShrink: 0, paddingRight: "28px", textAlign: "right" }}>
                  <span style={{ fontSize: "12px", fontWeight: "600", color: "#167485", letterSpacing: "0.3px", lineHeight: "1.6" }}>{item.year}</span>
                </div>
                <div className="timeline-dot" style={{ position: "absolute", left: "126px", top: "5px", width: "9px", height: "9px", borderRadius: "50%", background: "#4fc3d9" }} />
                <div className="timeline-content" style={{ paddingLeft: "36px" }}>
                  <p style={{ margin: "0 0 4px 0", fontWeight: "600", fontSize: "15px", color: "#1a1a1a", lineHeight: "1.4" }}>{item.title}</p>
                  {Array.isArray(item.institution)
                    ? item.institution.map((inst, i) => (
                        <p key={i} style={{ margin: i === 0 ? "0 0 2px 0" : "0 0 2px 0", fontSize: "13px", color: "#6b6b6b" }}>• {inst}</p>
                      ))
                    : <p style={{ margin: "0", fontSize: "13px", color: "#6b6b6b" }}>{item.institution}</p>
                  }
                </div>
              </div>
            ))}
          </div>

          {/* International training */}
          <div style={{ background: "white", borderLeft: "3px solid #4fc3d9", padding: "16px 24px", marginBottom: "80px", borderRadius: "0 2px 2px 0", maxWidth: "700px", margin: "0 auto 80px" }}>
            <p style={{ margin: 0, fontSize: "14px", color: "#666", lineHeight: "1.7", fontStyle: "italic" }}>{t.credentials.intl}</p>
          </div>

          {/* Academic + Research — two columns */}
          <div className="exp-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px", marginBottom: "60px" }}>
            <div style={{ background: "white", padding: "32px", borderRadius: "2px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
              <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1a1a1a", marginBottom: "6px", paddingBottom: "12px", borderBottom: "2px solid #4fc3d9" }}>{t.credentials.academic.title}</h3>
              <p style={{ fontSize: "13px", color: "#6b6b6b", marginBottom: "16px", marginTop: "10px" }}>{t.credentials.academic.intro}</p>
              <ul style={{ margin: 0, padding: "0 0 0 18px" }}>
                {t.credentials.academic.items.map((item, i) => (
                  <li key={i} style={{ fontSize: "14px", color: "#666", lineHeight: "1.7", marginBottom: "8px" }}>{item}</li>
                ))}
              </ul>
            </div>
            <div style={{ background: "white", padding: "32px", borderRadius: "2px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
              <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1a1a1a", marginBottom: "6px", paddingBottom: "12px", borderBottom: "2px solid #4fc3d9" }}>{t.credentials.research.title}</h3>
              <p style={{ fontSize: "13px", color: "#6b6b6b", marginBottom: "16px", marginTop: "10px" }}>{t.credentials.research.intro}</p>
              <ul style={{ margin: 0, padding: "0 0 0 18px" }}>
                {t.credentials.research.items.map((item, i) => (
                  <li key={i} style={{ fontSize: "14px", color: "#666", lineHeight: "1.7", marginBottom: "8px" }}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          {/* Conferences */}
          <div style={{ background: "white", padding: "32px", borderRadius: "2px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", marginBottom: "80px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1a1a1a", marginBottom: "20px", paddingBottom: "12px", borderBottom: "2px solid #4fc3d9" }}>{t.credentials.conferences.title}</h3>
            <ul className="conf-list" style={{ margin: 0, padding: "0 0 0 18px", columns: 2 }}>
              {t.credentials.conferences.items.map((item, i) => (
                <li key={i} style={{ fontSize: "14px", color: "#666", lineHeight: "1.7", marginBottom: "8px", breakInside: "avoid" }}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Professional Philosophy */}
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <h3 style={{ fontSize: "32px", fontWeight: "400", marginBottom: "12px", color: "#1a1a1a" }}>{t.credentials.philosophy.title}</h3>
            <p style={{ fontSize: "15px", color: "#6b6b6b" }}>{t.credentials.philosophy.subtitle}</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "24px" }}>
            {t.credentials.philosophy.items.map((item, i) => (
              <div key={i} style={{ background: "white", padding: "28px 20px", borderRadius: "2px", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", textAlign: "center", transition: "all 0.3s ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.05)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#4fc3d9", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: "#1a1a1a", fontWeight: "700", fontSize: "16px" }}>{i + 1}</div>
                <p style={{ margin: "0 0 8px 0", fontWeight: "600", fontSize: "14px", color: "#1a1a1a" }}>{item.title}</p>
                <p style={{ margin: 0, fontSize: "13px", color: "#6b6b6b", lineHeight: "1.5" }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== GALLERY ===== */}
      <section id="gallery" className="section-pad" style={{ padding: "100px 40px", background: "white" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <h2 className="section-title" style={{ fontSize: "42px", fontWeight: "400", marginBottom: "16px", color: "#1a1a1a" }}>
              {lang === "ro" ? "Galerie — În Sala de Operație" : "Gallery — In the Operating Room"}
            </h2>
          </div>

          {/* Carousel */}
          <div style={{ position: "relative", userSelect: "none" }}>
            {/* Image container */}
            <div
              style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden", borderRadius: "4px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", background: "#111" }}
              onTouchStart={e => { galleryTouchX.current = e.touches[0].clientX; }}
              onTouchEnd={e => {
                if (galleryTouchX.current === null) return;
                const diff = galleryTouchX.current - e.changedTouches[0].clientX;
                if (Math.abs(diff) > 40) {
                  setGalleryIndex(i => diff > 0
                    ? Math.min(i + 1, galleryImages.length - 1)
                    : Math.max(i - 1, 0)
                  );
                }
                galleryTouchX.current = null;
              }}
            >
              {galleryImages.map((img, i) => (
                <img
                  key={img.name}
                  src={imgSrc(img.name)}
                  srcSet={imgSrcSet(img.name)}
                  sizes="(max-width: 980px) calc(100vw - 80px), 900px"
                  loading="lazy"
                  decoding="async"
                  alt={`${ALT_PREFIX[lang]} — ${lang === "ro" ? img.captionRo : img.captionEn}`}
                  style={{
                    position: "absolute", inset: 0,
                    width: "100%", height: "100%",
                    objectFit: "cover", objectPosition: "center top",
                    opacity: i === galleryIndex ? 1 : 0,
                    transition: "opacity 0.5s ease",
                    pointerEvents: "none",
                  }}
                />
              ))}

              {/* Left arrow */}
              {galleryIndex > 0 && (
                <button
                  onClick={() => setGalleryIndex(i => i - 1)}
                  aria-label={lang === "ro" ? "Poza anterioară" : "Previous photo"}
                  style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", color: "white", border: "none", borderRadius: "50%", width: "44px", height: "44px", fontSize: "20px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.70)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(0,0,0,0.45)"}
                >
                  ‹
                </button>
              )}

              {/* Right arrow */}
              {galleryIndex < galleryImages.length - 1 && (
                <button
                  onClick={() => setGalleryIndex(i => i + 1)}
                  aria-label={lang === "ro" ? "Poza următoare" : "Next photo"}
                  style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", color: "white", border: "none", borderRadius: "50%", width: "44px", height: "44px", fontSize: "20px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.70)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(0,0,0,0.45)"}
                >
                  ›
                </button>
              )}

              {/* Counter */}
              <div style={{ position: "absolute", top: "14px", right: "16px", background: "rgba(0,0,0,0.45)", color: "white", fontSize: "12px", padding: "4px 10px", borderRadius: "20px", letterSpacing: "0.5px" }}>
                {galleryIndex + 1} / {galleryImages.length}
              </div>
            </div>

            {/* Caption */}
            <p style={{ marginTop: "20px", textAlign: "center", fontSize: "14px", color: "#666", fontStyle: "italic", lineHeight: "1.6", minHeight: "44px" }}>
              {lang === "ro" ? galleryImages[galleryIndex].captionRo : galleryImages[galleryIndex].captionEn}
            </p>

            {/* Dots */}
            {/* 8px dot inside 8px of transparent padding: a 24px tap target that still looks small */}
            <div style={{ display: "flex", justifyContent: "center", gap: "2px", marginTop: "8px" }}>
              {galleryImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setGalleryIndex(i)}
                  aria-label={`${i + 1} / ${galleryImages.length}: ${lang === "ro" ? img.captionRo : img.captionEn}`}
                  aria-current={i === galleryIndex || undefined}
                  style={{ width: i === galleryIndex ? "24px" : "8px", height: "8px", boxSizing: "content-box", padding: "8px", borderRadius: "12px", border: "none", backgroundColor: i === galleryIndex ? "#4fc3d9" : "#ccc", backgroundClip: "content-box", cursor: "pointer", transition: "all 0.3s ease" }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== COMMENTS ===== */}
      <section id="comments" className="section-pad" style={{ padding: "100px 40px", background: "white" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <h2 className="section-title" style={{ fontSize: "42px", fontWeight: "400", marginBottom: "16px", color: "#1a1a1a" }}>
            {t.comments.title}
          </h2>

          <div style={{ marginBottom: "40px" }}>
            <input
              type="text"
              value={commentUsername}
              onChange={(e) => setCommentUsername(e.target.value)}
              placeholder={lang === "ro" ? "Numele tău *" : "Your name *"}
              className="mobile-input"
              style={{
                width: "100%",
                padding: "10px 12px",
                marginBottom: "8px",
                borderRadius: "4px",
                border: "1px solid #e0dcd5",
                fontFamily: "inherit",
                fontSize: "15px",
              }}
            />
            <textarea
              value={commentInput}
              onChange={(e) => setCommentInput(e.target.value)}
              placeholder={t.comments.placeholder}
              className="mobile-input"
              style={{
                width: "100%",
                padding: "12px",
                minHeight: "80px",
                borderRadius: "4px",
                border: "1px solid #e0dcd5",
                fontFamily: "inherit",
                fontSize: "15px",
                resize: "vertical",
              }}
            />
            <button
              onClick={handleAddComment}
              style={{
                marginTop: "12px",
                background: "#4fc3d9",
                color: "#1a1a1a",
                padding: "10px 24px",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "13px",
              }}
            >
              {t.comments.submit}
            </button>
          </div>

          {filteredComments.slice(0, commentsVisible).map(c => (
              <div
                key={c.id}
                style={{
                  borderBottom: "1px solid #e0dcd5",
                  padding: "20px 0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1 }}>
                  <p style={{ margin: "0 0 4px 0", fontSize: "13px", fontWeight: "600", color: "#167485" }}>
                    {c.username || "Anonymous"}
                  </p>
                  {editingItem?.type === 'comment' && editingItem?.id === c.id ? (
                    <div style={{ marginBottom: "8px" }}>
                      <textarea
                        value={editingItem.text}
                        onChange={e => setEditingItem({ ...editingItem, text: e.target.value })}
                        style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #4fc3d9", fontFamily: "inherit", fontSize: "15px", resize: "vertical", marginBottom: "8px", boxSizing: "border-box" }}
                      />
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button onClick={() => saveEditComment(c.id, editingItem.text)} style={{ background: "#4fc3d9", color: "#1a1a1a", border: "none", padding: "6px 14px", cursor: "pointer", borderRadius: "4px", fontWeight: "600", fontSize: "13px" }}>{t.comments.save}</button>
                        <button onClick={() => setEditingItem(null)} style={{ background: "#999", color: "white", border: "none", padding: "6px 14px", cursor: "pointer", borderRadius: "4px", fontSize: "13px" }}>{t.comments.cancel}</button>
                      </div>
                    </div>
                  ) : (
                    <p style={{ margin: "0 0 8px 0", color: "#2c2c2c" }}>{c.text}</p>
                  )}
                  <p style={{ margin: "0 0 10px 0", fontSize: "12px", color: "#6b6b6b" }}>
                    {formatDate(c.created_at)} {!c.approved && ` • ${t.comments.pending}`}
                  </p>
                  {c.approved && <ReactionBar entityType="comment" entity={c} userReactions={userReactions} onReact={handleReact} />}
                </div>
                {isAdmin && (
                  <div style={{ display: "flex", gap: "8px", marginLeft: "12px" }}>
                    {!c.approved && (
                      <button
                        onClick={() => approveComment(c.id)}
                        style={{
                          background: "#4fc3d9",
                          color: "#1a1a1a",
                          border: "none",
                          padding: "6px 12px",
                          cursor: "pointer",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FaCheck size={12} /> {t.comments.approve}
                      </button>
                    )}
                    {c.approved && (
                      <button
                        onClick={() => setEditingItem({ type: 'comment', id: c.id, text: c.text })}
                        style={{ background: "#ff9800", color: "white", border: "none", padding: "6px 12px", cursor: "pointer", borderRadius: "4px", display: "flex", alignItems: "center", gap: "6px" }}
                      >
                        <FaPencilAlt size={12} />
                      </button>
                    )}
                    <button
                      onClick={() => deleteComment(c.id)}
                      style={{
                        background: "#f44336",
                        color: "white",
                        border: "none",
                        padding: "6px 12px",
                        cursor: "pointer",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <FaTrash size={12} /> {t.comments.delete}
                    </button>
                  </div>
                )}
              </div>
            ))}
          {filteredComments.length > 5 && (
            <>
              <p style={{ fontSize: "12px", color: "#6b6b6b", textAlign: "center", margin: "12px 0 4px" }}>
                {lang === "ro"
                  ? `Se afișează ${Math.min(commentsVisible, filteredComments.length)} din ${filteredComments.length} comentarii`
                  : `Showing ${Math.min(commentsVisible, filteredComments.length)} of ${filteredComments.length} comments`}
              </p>
              <div style={{ display: "flex", gap: "8px" }}>
                {filteredComments.length > commentsVisible && (
                  <button
                    onClick={() => setCommentsVisible(v => v + 5)}
                    style={{ flex: 1, padding: "12px", background: "#4fc3d9", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px", color: "#1a1a1a", fontFamily: "inherit", fontWeight: "600", letterSpacing: "0.5px" }}
                  >
                    {t.comments.loadMore} ↓
                  </button>
                )}
                {commentsVisible > 5 && (
                  <button
                    onClick={() => setCommentsVisible(5)}
                    style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid #4fc3d9", borderRadius: "4px", cursor: "pointer", fontSize: "13px", color: "#167485", fontFamily: "inherit", fontWeight: "600", letterSpacing: "0.5px" }}
                  >
                    {t.comments.loadLess} ↑
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ===== Q&A ===== */}
      <section id="qa" className="section-pad" style={{ padding: "100px 40px", background: "#fafaf8" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <h2 className="section-title" style={{ fontSize: "42px", fontWeight: "400", marginBottom: "8px", color: "#1a1a1a" }}>
            {t.qa.title}
          </h2>
          <p style={{ fontSize: "16px", color: "#6b6b6b", marginBottom: "40px" }}>
            {t.qa.subtitle}
          </p>

          <div style={{ marginBottom: "40px" }}>
            <input
              type="text"
              value={questionUsername}
              onChange={(e) => setQuestionUsername(e.target.value)}
              placeholder={lang === "ro" ? "Numele tău *" : "Your name *"}
              className="mobile-input"
              style={{
                width: "100%",
                padding: "10px 12px",
                marginBottom: "8px",
                borderRadius: "4px",
                border: "1px solid #e0dcd5",
                fontFamily: "inherit",
                fontSize: "15px",
              }}
            />
            <textarea
              value={questionInput}
              onChange={(e) => setQuestionInput(e.target.value)}
              placeholder={t.qa.placeholder}
              className="mobile-input"
              style={{
                width: "100%",
                padding: "12px",
                minHeight: "80px",
                borderRadius: "4px",
                border: "1px solid #e0dcd5",
                fontFamily: "inherit",
                fontSize: "15px",
                resize: "vertical",
              }}
            />
            <button
              onClick={handleAddQuestion}
              style={{
                marginTop: "12px",
                background: "#4fc3d9",
                color: "#1a1a1a",
                padding: "10px 24px",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "13px",
              }}
            >
              {t.qa.submit}
            </button>
          </div>

          {filteredQuestions.slice(0, questionsVisible).map(q => (
              <div
                key={q.id}
                style={{
                  background: "white",
                  borderRadius: "4px",
                  padding: "20px",
                  marginBottom: "16px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
                onTouchStart={(e) => {
                  questionTouchX.current[q.id] = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                }}
                onTouchEnd={(e) => {
                  const start = questionTouchX.current[q.id];
                  if (!start) return;
                  const dx = e.changedTouches[0].clientX - start.x;
                  const dy = Math.abs(e.changedTouches[0].clientY - start.y);
                  delete questionTouchX.current[q.id];
                  if (Math.abs(dx) > 60 && Math.abs(dx) > dy) {
                    setExpandedQuestion(dx > 0 ? q.id : null);
                  }
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    cursor: "pointer",
                  }}
                  onClick={() => setExpandedQuestion(expandedQuestion === q.id ? null : q.id)}
                >
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: "0 0 4px 0", fontSize: "13px", fontWeight: "600", color: "#167485" }}>
                      {q.username || "Anonymous"}
                    </p>
                    {editingItem?.type === 'question' && editingItem?.id === q.id ? (
                      <div onClick={e => e.stopPropagation()} style={{ marginBottom: "8px" }}>
                        <textarea
                          value={editingItem.text}
                          onChange={e => setEditingItem({ ...editingItem, text: e.target.value })}
                          style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #4fc3d9", fontFamily: "inherit", fontSize: "15px", resize: "vertical", marginBottom: "8px", boxSizing: "border-box" }}
                        />
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button onClick={() => saveEditQuestion(q.id, editingItem.text)} style={{ background: "#4fc3d9", color: "#1a1a1a", border: "none", padding: "6px 14px", cursor: "pointer", borderRadius: "4px", fontWeight: "600", fontSize: "13px" }}>{t.comments.save}</button>
                          <button onClick={() => setEditingItem(null)} style={{ background: "#999", color: "white", border: "none", padding: "6px 14px", cursor: "pointer", borderRadius: "4px", fontSize: "13px" }}>{t.comments.cancel}</button>
                        </div>
                      </div>
                    ) : (
                      <p style={{ margin: "0 0 8px 0", fontWeight: "600", color: "#1a1a1a" }}>
                        {q.text}
                      </p>
                    )}
                    <p style={{ margin: "0 0 10px 0", fontSize: "12px", color: "#6b6b6b" }}>
                      {formatDate(q.created_at)} {!q.approved && ` • ${t.qa.pending}`} • {q.replies.filter(r => r.approved || isAdmin).length} {t.qa.replies}
                    </p>
                    {q.approved && (
                      <div onClick={e => e.stopPropagation()}>
                        <ReactionBar entityType="question" entity={q} userReactions={userReactions} onReact={handleReact} />
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: "8px", marginLeft: "12px" }}>
                      {!q.approved && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            approveQuestion(q.id);
                          }}
                          style={{
                            background: "#4fc3d9",
                            color: "#1a1a1a",
                            border: "none",
                            padding: "6px 12px",
                            cursor: "pointer",
                            borderRadius: "4px",
                          }}
                        >
                          <FaCheck size={12} />
                        </button>
                      )}
                      {q.approved && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingItem({ type: 'question', id: q.id, text: q.text });
                          }}
                          style={{ background: "#ff9800", color: "white", border: "none", padding: "6px 12px", cursor: "pointer", borderRadius: "4px" }}
                        >
                          <FaPencilAlt size={12} />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteQuestion(q.id);
                        }}
                        style={{
                          background: "#f44336",
                          color: "white",
                          border: "none",
                          padding: "6px 12px",
                          cursor: "pointer",
                          borderRadius: "4px",
                        }}
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  )}
                </div>

                {expandedQuestion === q.id && (
                  <div style={{ marginTop: "20px", borderTop: "1px solid #e0dcd5", paddingTop: "20px" }}>
                    {/* Replies */}
                    {q.replies
                      .filter(r => r.approved || isAdmin)
                      .map(r => (
                        <div
                          key={r.id}
                          style={{
                            background: "#fafaf8",
                            padding: "12px",
                            borderRadius: "4px",
                            marginBottom: "12px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <p style={{ margin: "0 0 4px 0", fontSize: "13px", fontWeight: "600", color: "#167485" }}>
                              {r.username || "Dr. Georgescu"}
                            </p>
                            {editingItem?.type === 'reply' && editingItem?.id === r.id ? (
                              <div style={{ marginBottom: "6px" }}>
                                <textarea
                                  value={editingItem.text}
                                  onChange={e => setEditingItem({ ...editingItem, text: e.target.value })}
                                  style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #4fc3d9", fontFamily: "inherit", fontSize: "14px", resize: "vertical", marginBottom: "8px", boxSizing: "border-box" }}
                                />
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <button onClick={() => saveEditReply(q.id, r.id, editingItem.text)} style={{ background: "#4fc3d9", color: "#1a1a1a", border: "none", padding: "4px 12px", cursor: "pointer", borderRadius: "4px", fontWeight: "600", fontSize: "12px" }}>{t.comments.save}</button>
                                  <button onClick={() => setEditingItem(null)} style={{ background: "#999", color: "white", border: "none", padding: "4px 12px", cursor: "pointer", borderRadius: "4px", fontSize: "12px" }}>{t.comments.cancel}</button>
                                </div>
                              </div>
                            ) : (
                              <p style={{ margin: "0 0 6px 0", color: "#2c2c2c" }}>{r.text}</p>
                            )}
                            <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: "#6b6b6b" }}>
                              {formatDate(r.created_at)} {!r.approved && ` • ${t.qa.pending}`}
                            </p>
                            {r.approved && <ReactionBar entityType="reply" entity={r} userReactions={userReactions} onReact={handleReact} />}
                          </div>
                          {isAdmin && (
                            <div style={{ display: "flex", gap: "8px", marginLeft: "12px" }}>
                              {!r.approved && (
                                <button
                                  onClick={() => approveReply(q.id, r.id)}
                                  style={{
                                    background: "#4fc3d9",
                                    color: "#1a1a1a",
                                    border: "none",
                                    padding: "4px 8px",
                                    cursor: "pointer",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                  }}
                                >
                                  <FaCheck size={10} />
                                </button>
                              )}
                              {r.approved && (
                                <button
                                  onClick={() => setEditingItem({ type: 'reply', id: r.id, qid: q.id, text: r.text })}
                                  style={{ background: "#ff9800", color: "white", border: "none", padding: "4px 8px", cursor: "pointer", borderRadius: "4px" }}
                                >
                                  <FaPencilAlt size={10} />
                                </button>
                              )}
                              <button
                                onClick={() => deleteReply(q.id, r.id)}
                                style={{
                                  background: "#f44336",
                                  color: "white",
                                  border: "none",
                                  padding: "4px 8px",
                                  cursor: "pointer",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                }}
                              >
                                <FaTrash size={10} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}

                    {/* Reply input */}
                    <div style={{ marginTop: "12px" }}>
                      <textarea
                        value={replyInput[q.id] || ""}
                        onChange={(e) => setReplyInput({ ...replyInput, [q.id]: e.target.value })}
                        placeholder={t.qa.replyPlaceholder}
                        className="mobile-input"
                        style={{
                          width: "100%",
                          padding: "8px",
                          minHeight: "60px",
                          borderRadius: "4px",
                          border: "1px solid #e0dcd5",
                          fontFamily: "inherit",
                          fontSize: "15px",
                          resize: "vertical",
                        }}
                      />
                      <button
                        onClick={() => handleAddReply(q.id)}
                        style={{
                          marginTop: "8px",
                          background: "#4fc3d9",
                          color: "#1a1a1a",
                          padding: "8px 16px",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontWeight: "600",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FaReply size={12} /> {t.qa.reply}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          {filteredQuestions.length > questionsVisible && (
            <>
              <p style={{ fontSize: "12px", color: "#6b6b6b", textAlign: "center", margin: "12px 0 4px" }}>
                {lang === "ro"
                  ? `Se afișează ${Math.min(questionsVisible, filteredQuestions.length)} din ${filteredQuestions.length} întrebări`
                  : `Showing ${Math.min(questionsVisible, filteredQuestions.length)} of ${filteredQuestions.length} questions`}
              </p>
              <button
                onClick={() => setQuestionsVisible(v => v + 5)}
                style={{ width: "100%", padding: "12px", background: "#4fc3d9", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px", color: "#1a1a1a", fontFamily: "inherit", fontWeight: "600", letterSpacing: "0.5px", marginTop: "4px" }}
              >
                {t.qa.loadMore} ↓
              </button>
            </>
          )}
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="section-pad" style={{ padding: "100px 40px", background: "#1a1a1a", color: "white", textAlign: "center" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "42px", fontWeight: "400", marginBottom: "16px", color: "white" }}>
            {t.contact.title}
          </h2>
          <p style={{ fontSize: "16px", color: "#aaa", marginBottom: "60px" }}>
            {t.contact.subtitle}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "40px", marginBottom: "60px" }}>
            <div>
              <div style={{ fontSize: "13px", letterSpacing: "1px", textTransform: "uppercase", color: "#4fc3d9", marginBottom: "8px", fontWeight: "600" }}>
                {lang === "ro" ? "Telefon" : "Phone"}
              </div>
              <a href="tel:+40745161261" style={{ fontSize: "16px", color: "white", textDecoration: "none", transition: "color 0.3s" }} onMouseEnter={(e) => { e.target.style.color = "#4fc3d9"; }} onMouseLeave={(e) => { e.target.style.color = "white"; }}>
                +40 (0) 745 161 261
              </a>
            </div>

            <div>
              <div style={{ fontSize: "13px", letterSpacing: "1px", textTransform: "uppercase", color: "#4fc3d9", marginBottom: "8px", fontWeight: "600" }}>
                {lang === "ro" ? "Email" : "Email"}
              </div>
              <a href="mailto:florin.georgescu1@yahoo.com" style={{ fontSize: "16px", color: "white", textDecoration: "none", transition: "color 0.3s" }} onMouseEnter={(e) => { e.target.style.color = "#4fc3d9"; }} onMouseLeave={(e) => { e.target.style.color = "white"; }}>
                {t.contact.email}
              </a>
            </div>

            <div>
              <div style={{ fontSize: "13px", letterSpacing: "1px", textTransform: "uppercase", color: "#4fc3d9", marginBottom: "8px", fontWeight: "600" }}>
                {lang === "ro" ? "Program" : "Hours"}
              </div>
              <p style={{ fontSize: "14px", color: "#aaa", margin: "0" }}>
                {t.contact.hours}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: "20px", justifyContent: "center", marginBottom: "40px" }}>
            <a href="https://wa.me/40745161261" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" title="WhatsApp" style={iconStyle}>
              <FaWhatsapp size={22} />
            </a>
            <a href="https://www.instagram.com/dr.florin.georgescu" target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="Instagram" style={iconStyle}>
              <FaInstagram size={22} />
            </a>
            <a href="https://www.facebook.com/teddy.georgescu" target="_blank" rel="noopener noreferrer" aria-label="Facebook" title="Facebook" style={iconStyle}>
              <FaFacebook size={22} />
            </a>
            <a href="https://www.tiktok.com/@dr.florin.georgescu" target="_blank" rel="noopener noreferrer" aria-label="TikTok" title="TikTok" style={iconStyle}>
              <FaTiktok size={22} />
            </a>
          </div>

          <button
            onClick={() => setShowGdpr(v => !v)}
            style={{ background: "none", border: "none", padding: "0", marginBottom: "20px", fontFamily: "inherit", fontSize: "12px", color: "#4fc3d9", textDecoration: "none", cursor: "pointer" }}
            onMouseEnter={(e) => { e.target.style.textDecoration = "underline"; }}
            onMouseLeave={(e) => { e.target.style.textDecoration = "none"; }}
          >
            {showGdpr ? t.gdpr.hideLabel : t.gdpr.showLabel}
          </button>

          <p
            onClick={() => !isAdmin && setShowAdminLogin(true)}
            style={{ fontSize: "12px", color: "#9a9a9a", borderTop: "1px solid #333", paddingTop: "20px", margin: "0", userSelect: "none" }}
          >
            © 2026 Dr. Teodor-Florin Georgescu. {lang === "ro" ? "Toate drepturile rezervate." : "All rights reserved."}
          </p>
        </div>
      </section>

      {/* ===== GDPR ===== */}
      {showGdpr && (
        <section id="gdpr" ref={gdprRef} className="section-pad" style={{ padding: "100px 40px", background: "#fafaf8" }}>
          <div style={{ maxWidth: "800px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "56px" }}>
              <h2 className="section-title" style={{ fontSize: "36px", fontWeight: "400", marginBottom: "16px", color: "#1a1a1a" }}>
                {t.gdpr.title}
              </h2>
              <p style={{ fontSize: "16px", color: "#6b6b6b", marginBottom: "8px" }}>
                {t.gdpr.subtitle}
              </p>
              <p style={{ fontSize: "12px", color: "#6b6b6b" }}>
                {t.gdpr.lastUpdated}
              </p>
            </div>

            {t.gdpr.sections.map((sec, i) => (
              <div key={i} style={{ marginBottom: "36px" }}>
                <h3 style={{ fontSize: "17px", fontWeight: "600", color: "#1a1a1a", marginBottom: "10px", paddingBottom: "8px", borderBottom: "2px solid #4fc3d9" }}>
                  {sec.heading}
                </h3>
                {Array.isArray(sec.body) ? (
                  <ul style={{ margin: "0", padding: "0 0 0 18px" }}>
                    {sec.body.map((item, j) => (
                      <li key={j} style={{ fontSize: "14px", color: "#666", lineHeight: "1.7", marginBottom: "6px" }}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: "0", fontSize: "14px", color: "#666", lineHeight: "1.7" }}>{sec.body}</p>
                )}
                {sec.note && (
                  <p style={{ margin: "10px 0 0", fontSize: "13px", color: "#6b6b6b", fontStyle: "italic", lineHeight: "1.6" }}>{sec.note}</p>
                )}
              </div>
            ))}

            <div style={{ textAlign: "center" }}>
              <button
                onClick={() => setShowGdpr(false)}
                style={{ background: "#1a1a1a", color: "white", border: "none", padding: "10px 28px", borderRadius: "4px", cursor: "pointer", fontWeight: "600", fontSize: "13px", fontFamily: "inherit" }}
              >
                {t.gdpr.hideLabel}
              </button>
            </div>
          </div>
        </section>
      )}

      </main>

      {/* ===== FLOATING WHATSAPP ===== */}
      <a
        href="https://wa.me/40738749466"
        style={{
          position: "fixed",
          bottom: "28px",
          right: "28px",
          background: "#25d366",
          color: "white",
          width: "56px",
          height: "56px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textDecoration: "none",
          fontSize: "24px",
          boxShadow: "0 4px 20px rgba(37, 211, 102, 0.4)",
          zIndex: 50,
          transition: "all 0.3s ease",
          border: "none",
          cursor: "pointer",
        }}
        onMouseEnter={(e) => { e.target.style.transform = "scale(1.1)"; e.target.style.boxShadow = "0 8px 28px rgba(37, 211, 102, 0.6)"; }}
        onMouseLeave={(e) => { e.target.style.transform = "scale(1)"; e.target.style.boxShadow = "0 4px 20px rgba(37, 211, 102, 0.4)"; }}
        title="WhatsApp"
        aria-label="WhatsApp"
      >
        💬
      </a>

      {submissionAlert && (
        <div
          onClick={() => setSubmissionAlert(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 500,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "white", borderRadius: "8px", padding: "40px 32px",
              maxWidth: "380px", width: "100%", textAlign: "center",
              boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
              animation: "fadeInScale 0.2s ease",
            }}
          >
            <div style={{
              width: "56px", height: "56px", borderRadius: "50%",
              background: "#e8f8f5", margin: "0 auto 20px",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FaCheck size={24} color="#2ecc71" />
            </div>
            <p style={{ margin: "0 0 24px", fontSize: "15px", lineHeight: "1.7", color: "#2c2c2c" }}>
              {submissionAlert}
            </p>
            <button
              onClick={() => setSubmissionAlert(null)}
              style={{
                background: "#4fc3d9", color: "#1a1a1a", border: "none",
                padding: "10px 32px", borderRadius: "4px", cursor: "pointer",
                fontWeight: "600", fontSize: "14px", fontFamily: "inherit",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div key={toast.id} style={{
          position: "fixed", bottom: "104px", right: "28px", zIndex: 200,
          background: "#1a1a1a", color: "white",
          padding: "14px 18px 10px", borderRadius: "6px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
          minWidth: "200px", maxWidth: "280px",
        }}>
          <p style={{ margin: "0 0 10px 0", fontSize: "14px", fontWeight: "500", letterSpacing: "0.2px" }}>
            {toast.message}
          </p>
          <div style={{ height: "3px", background: "#333", borderRadius: "2px", overflow: "hidden" }}>
            <div style={{ height: "100%", animation: "toastProgress 5s linear forwards" }} />
          </div>
        </div>
      )}

      {showAdminLogin && (
        <div
          onClick={() => { setShowAdminLogin(false); setAdminPassword(""); setAdminError(""); }}
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "white", padding: "40px", borderRadius: "4px", width: "340px", boxShadow: "0 24px 64px rgba(0,0,0,0.3)" }}
          >
            <h3 style={{ margin: "0 0 24px 0", fontSize: "18px", fontWeight: "600", color: "#1a1a1a" }}>
              {lang === "ro" ? "Autentificare Admin" : "Admin Login"}
            </h3>
            <input
              type="password"
              value={adminPassword}
              onChange={e => setAdminPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleAdminLogin()}
              placeholder={lang === "ro" ? "Parolă" : "Password"}
              autoFocus
              style={{ width: "100%", padding: "10px 12px", marginBottom: "12px", border: "1px solid #e0dcd5", borderRadius: "4px", fontFamily: "inherit", fontSize: "14px", boxSizing: "border-box" }}
            />
            {adminError && (
              <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#e53935" }}>{adminError}</p>
            )}
            <button
              onClick={handleAdminLogin}
              style={{ width: "100%", background: "#1a1a1a", color: "white", border: "none", padding: "12px", borderRadius: "4px", cursor: "pointer", fontWeight: "600", fontSize: "14px", fontFamily: "inherit" }}
            >
              {lang === "ro" ? "Intră" : "Login"}
            </button>
          </div>
        </div>
      )}

      {/* dangerouslySetInnerHTML, not a text child: the prerender (renderToString) would escape
          the quotes and ">" in these selectors, which a <style> element then reads literally. */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes toastProgress {
          0%   { width: 100%; background: #4fc3d9; }
          60%  { background: #f59e0b; }
          100% { width: 0%;   background: #ef4444; }
        }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInScale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @media (max-width: 768px) {
          header { padding: 0 20px; }
          .desktop-nav { display: none !important; }
          h1 { font-size: 36px !important; }
          h2 { font-size: 28px !important; }
          section { padding: 60px 20px !important; }
          section[style*="display: flex"] { flex-direction: column !important; }
          section[style*="display: flex"] > div { width: 100% !important; }
        }
      ` }} />
    </div>
  );
}